import {AfterViewInit, ChangeDetectorRef, Component, ElementRef, inject, model, OnInit, ViewChild} from '@angular/core';
import {FormControl, FormGroup, ReactiveFormsModule} from "@angular/forms";
import {TranslateService} from '@ngx-translate/core';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import * as uuid from 'uuid';
import {LabelOptions} from '@core/classes/options/label-options';
import {InputTypes} from '@core/enums';

export class TextAreaOptions {
  id?: string = uuid.v4();
  label?: LabelOptions;
  placeholder?: string;
  name?: string;
  type?: InputTypes = InputTypes.text;
  mode?: string;
  value?: string;
  pattern?: RegExp;
  inputClass?: string;
  onKeyDown?: (event: KeyboardEvent, value: string, input: HTMLInputElement) => void;
  onKeyUp?: (event: KeyboardEvent, value: string) => void;
  onInput?: (el: HTMLInputElement) => any;
  onBlur?: (value: string, input: HTMLInputElement) => void;
  resizeOnInput?: boolean;
  maxSize?: number;
  initialValue?: any;
  formControl?: FormControl<any>;
  formGroup?: FormGroup;
  formControlName?: string;
  required?: boolean | true;
  rows?: number;
  disabled?: boolean;

  constructor(args?: TextAreaOptions) {
    this.id = args?.id || uuid.v4();
    this.label = args?.label;
    this.placeholder = args?.placeholder || '';
    this.name = args?.name || '';
    this.type = args?.type || InputTypes.text;
    this.mode = args?.mode || '';
    this.value = args?.value || '';
    this.pattern = args?.pattern;
    this.inputClass = args?.inputClass || '';
    this.onKeyDown = args?.onKeyDown;
    this.onKeyUp = args?.onKeyUp;
    this.onInput = args?.onInput;
    this.onBlur = args?.onBlur;
    this.resizeOnInput = args?.resizeOnInput || false;
    this.maxSize = args?.maxSize;
    this.initialValue = args?.initialValue;
    this.formControl = args?.formControl;
    this.formGroup = args?.formGroup;
    this.formControlName = args?.formControlName;
    this.required = args?.required !== undefined ? args?.required : false;
    this.rows = args?.rows || 5;
    this.disabled = args?.disabled !== undefined ? args?.disabled : false;
  }
}

@UntilDestroy()
@Component({
  selector: 'ncp-text-area',
    imports: [
        ReactiveFormsModule
    ],
  templateUrl: './text-area.component.html',
  styleUrl: './text-area.component.scss',
})
export class TextAreaComponent implements OnInit, AfterViewInit {
  @ViewChild('el') public el?: ElementRef<HTMLTextAreaElement>;
  private readonly changeDetectorRef: ChangeDetectorRef = inject(ChangeDetectorRef);
  private readonly translate = inject(TranslateService);
  protected translations = this.translate.instant('textarea');
  protected invalidMessage: string = '';

  options = model<TextAreaOptions>(new TextAreaOptions({}));

  // options = input(new InputOptions(), {
  //   transform: (value: InputOptions) => {
  //     if (!value) {
  //       return new InputOptions({});
  //     }
  //
  //     if (value.inputClass) {
  //       value.inputClass = this.options().inputClass + ' ' + value.inputClass;
  //     }
  //
  //     if (value.initialValue) {
  //       if (value.formGroup && value.formControlName) {
  //         value.formGroup.controls[value.formControlName].setValue(value.initialValue);
  //       } else if (value.formControlName) {
  //         value.formControl?.setValue(value.initialValue);
  //       }
  //     }
  //
  //     if (value.resizeOnInput) {
  //       this.el?.nativeElement.setAttribute('size', '' + this.el?.nativeElement.value.length);
  //     }
  //
  //     if (value.maxSize) {
  //       this.el?.nativeElement.setAttribute('size', '' + value.maxSize!);
  //     }
  //
  //     return value;
  //   },
  // });

  ngOnInit() {
    this.reset();

    if (this.options().initialValue) {
      if (this.options().formGroup && this.options().formControlName) {
        this.options().formGroup?.controls[this.options()?.formControlName!].setValue(
          this.options().initialValue
        );
      } else if (this.options().formControlName) {
        this.options().formControl?.setValue(this.options().initialValue);
      }
    }

    if (this.options().resizeOnInput) {
      this.el?.nativeElement.setAttribute('size', '' + this.el?.nativeElement.value.length);
    }

    if (this.options().maxSize) {
      this.el?.nativeElement.setAttribute('size', '' + this.options().maxSize!);
    }

    this.translate.onLangChange.pipe(untilDestroyed(this)).subscribe((event) => {
      this.translations = this.translate.instant('textarea');
    });
  }

  detectChanges() {
    this.changeDetectorRef.detectChanges();
  }

  ngAfterViewInit() {
    if (this.options().resizeOnInput) {
      this.el?.nativeElement.setAttribute('size', '' + this.el?.nativeElement.value.length);
    }
    if (this.options().maxSize) {
      this.el?.nativeElement.setAttribute('size', this.options().maxSize!.toString());
    }
  }

  onKeyDown(event: KeyboardEvent, value: string, input: HTMLInputElement) {
    if(this.options().pattern) {
      if(!this.options().pattern!.test(value.replace(/[^a-zA-Z0-9,_. ]/g, "") + event.key)) {

        event.preventDefault()
      }
    }
    if (this.options()?.onKeyDown) {
      this.options().onKeyDown?.(event, value, input);
    }
  }

  onKeyUp(event: KeyboardEvent, value: string) {
    if(this.options().pattern) {

    }
    if (this.options()?.onKeyUp) {
      this.options()?.onKeyUp?.(event, value);
    }
  }

  onInput(input: HTMLInputElement) {
    if (this.options()?.onInput) {
      this.options().value = this.options().onInput?.(input);
      input.value = this.options().value || '';
    }
  }

  onBlur(value: string, input: HTMLInputElement) {
    if(this.options()?.pattern) {
      input.value = input.value.replace(/[^a-zA-Z0-9,_.\n ]/g, "");
      this.options().value = input.value;
      if(this.options().formControl) {
        this.options().formControl?.setValue(input.value);
      }
    }
    if (this.options()?.onBlur) {
      this.options()?.onBlur?.(value, input);
    }
  }

  enable() {
    if(this.options().formControl) {
      this.options().formControl?.enable();
    }
  }

  disable() {
    if(this.options().formControl) {
      this.options().formControl?.disable();
    }
  }

  reset() {
    if (this.options().initialValue) {
      if (this.options().formGroup && this.options().formControlName) {
        this.options().formGroup?.controls[this.options()?.formControlName!].setValue(
          this.options().initialValue
        );
      } else if (this.options().formControlName) {
        this.options().formControl?.setValue(this.options().initialValue);
      }
    }

    if(this.options().disabled) {
      this.disable();
    }

    if (this.options().resizeOnInput) {
      this.el?.nativeElement.setAttribute('size', '' + this.el?.nativeElement.value.length);
    }

    if (this.options().maxSize) {
      this.el?.nativeElement.setAttribute('size', '' + this.options().maxSize!);
    }
  }

  protected readonly oninput = oninput;
}
